/**
 * A list's sort order and, on phones (where there's no sidebar of them), its
 * filters: a menu (ui/Menu: iOS's own in the app) from a sort button
 */
import { effect } from "@preact/signals";
import * as preact from "preact";

import * as deviceDetect from "deviceDetect";
import Button from "ui/Button";
import Menu, { type MenuSection } from "ui/Menu";

export interface MenuOption {
  value: string;
  label: string;
}

interface Props {
  /** None when the list can't be sorted (deleted words) */
  sortOptions: MenuOption[];
  sortBy: string;
  /** The sort's direction, shown beside it, when picking it again reverses it */
  sortAscending?: boolean;
  onSort: (value: string) => void;
  /** Where the list has no sidebar of filters (phones), the same choices */
  filters?: {
    options: MenuOption[];
    selected: string;
    onSelect: (value: string) => void;
  };
}

export default class SortAndFilterMenu extends preact.Component<Props> {
  private disposeViewportWatch?: () => void;

  componentDidMount() {
    this.disposeViewportWatch = effect(() => {
      void deviceDetect.smallViewport.value;
      this.forceUpdate();
    });
  }

  componentWillUnmount() {
    this.disposeViewportWatch?.();
  }

  private sections(small: boolean): MenuSection[] {
    const { sortOptions, sortBy, sortAscending, onSort, filters } = this.props;
    const sections: MenuSection[] = [
      {
        title: "Sort by",
        items: sortOptions.map((option) => ({
          label: option.label,
          checked: option.value === sortBy,
          direction:
            option.value === sortBy && sortAscending !== undefined
              ? sortAscending
                ? "ascending"
                : "descending"
              : undefined,
          onSelect: () => onSort(option.value),
        })),
      },
    ];
    if (filters && small) {
      sections.push({
        title: "Show",
        items: filters.options.map((option) => ({
          label: option.label,
          checked: option.value === filters.selected,
          onSelect: () => filters.onSelect(option.value),
        })),
      });
    }
    return sections;
  }

  render() {
    const small = deviceDetect.smallViewport.value;
    const sections = this.sections(small);
    if (sections.every((section) => section.items.length === 0)) {
      return null;
    }
    const { filters, sortOptions, sortBy } = this.props;
    const sortLabel = sortOptions.find(
      (option) => option.value === sortBy
    )?.label;
    return (
      <Menu
        class="sortAndFilterMenu"
        sections={sections}
        renderButton={(buttonProps) => (
          <Button
            {...buttonProps}
            icon="icon-sort-by-attributes"
            aria-label="Sort and filter"
            active={
              small &&
              !!filters &&
              filters.selected !== filters.options[0].value
            }
          >
            {small ? undefined : sortLabel}
          </Button>
        )}
      />
    );
  }
}
